
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { DictType } from "@pma/types"
import { useEffect, useState } from "react"
import { Button } from "@components/common/button"
import { Input } from "../input"
import useTranslator from "@pma/hooks/use-translator"

interface DataTablePaginationProps<TData> {
  data: DictType
  setPagination: any
  paginationData: any
}

export function DataTablePagination<TData>({
  setPagination,
  paginationData,
}: DataTablePaginationProps<TData>) {
  const [pageNo, setPageNo] = useState<any>(1)
  const [showMaxError, setShowMaxError] = useState(false)
  const [showMinError, setMinError] = useState(false)
  const { translate, locale } = useTranslator()
  const pageSize = paginationData.pageSize
  const maxPage = paginationData.pageTotal

  const handlePageNumberSubmit = () => {
    setPagination((prev: any) => ({
      ...prev,
      pageIndex: pageNo,
    }))
  }

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      handlePageNumberSubmit()
    }
  }

  const handleChange = (e: any) => {
    const { value } = e.target as any

    const no = parseInt(value)

    if (showMaxError) {
      setShowMaxError(false)
    } else if (showMinError) {
      setMinError(false)
    }

    if (no === 0) {
      setMinError(true)
    } else if (no <= maxPage) {
      setPageNo(no)
    } else if (no > maxPage) {
      setShowMaxError(true)
    }
  }

  useEffect(() => {
    if (paginationData.curPage) {
      setPageNo(paginationData.curPage)
    }
  }, [paginationData])

  return (
    <>
      <div className='flex items-center justify-end gap-3 px-2 py-2'>
        <div className='flex items-center justify-between space-x-6 lg:space-x-8'>
          <div className='hidden items-center justify-center space-x-2 md:flex'>
            <p className='text-sm font-medium'>{translate('Rows per page')}</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                setPagination((prev: any) => ({
                  ...prev,
                  pageSize: value,
                }))
              }}
            >
              <SelectTrigger className='h-8 w-[70px]'>
                <SelectValue className='h-[35px]' />
              </SelectTrigger>
              <SelectContent side='top'>
                {[10, 25, 30, 40, 50, 100, 200].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className='flex items-center gap-2 space-x-2'>
          <Button
            variant='outline'
            className='h-[35px] w-max px-3 py-2'
            onClick={() => {
              setPagination((prev: any) => ({
                ...prev,
                pageIndex: prev.pageIndex - 1,
              }))
            }}
            disabled={!paginationData.prevPage}
            title='Previous Page'
          >
            <svg
              className='me-2 h-3.5 w-3.5 rtl:rotate-180'
              aria-hidden='true'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 14 10'
            >
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M13 5H1m0 0 4 4M1 5l4-4'
              />
            </svg>
            <span>{translate('Previous')}</span>
          </Button>
          <div className='flex w-5 items-center justify-center'>
            <Input
              type='number'
              className='h-[35px] w-[50px] px-3 py-2 text-center outline-none'
              min={1}
              max={paginationData.pageTotal}
              onKeyDown={(e) => handleKeyDown(e)}
              onChange={handleChange}
              value={pageNo}
            />
          </div>
          <Button
            variant='outline'
            className='h-[35px] w-max px-3 py-2'
            onClick={() => {
              setPagination((prev: any) => {
                return {
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }
              })
            }}
            disabled={!paginationData.nextPage}
            title='Next Page'
          >
            <span>{translate('Next')}</span>
            <svg
              className='ms-2 h-3.5 w-3.5 rtl:rotate-180'
              aria-hidden='true'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 14 10'
            >
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M1 5h12m0 0L9 1m4 4L9 9'
              />
            </svg>
          </Button>
        </div>

        <div className='flex text-sm text-muted-foreground'>
          {paginationData.itemsReceived} {translate('of')} {paginationData.itemsTotal} {translate('Records')}
        </div>
      </div>
      {showMaxError && (
        <div className={`absolute bottom-[-30px] ${locale === 'he' ? 'left-[10%]' : 'right-[10%]'} `}>
          <p className='text-red-600'>{`${translate("only")} ${maxPage} ${translate("pages available")}`}</p>
        </div>
      )}
      {showMinError && (
        <div className={`absolute bottom-[-30px] ${locale === 'he' ? 'left-[10%]' : 'right-[10%]'} `}>
          <p className='text-red-600'>{`${translate("Please enter more than 0")}`}</p>
        </div>
      )}
    </>
  )
}
